import React, { useContext } from "react";
import { Modal, Checkbox, Radio, Button } from 'antd';
import AppContext from "../../context/Context";

const VIEW_KEYS = {
    position: ["name", "expiry", "strikePrice", "instrumentType", 'realizedPNL', 'unrealizedPNL', 'riskActions'],
    adjustments: ['realizedPNL', 'unrealizedPNL', "add", "exit", "reverse", "riskONriskOff", "riskActions"],
    greeks: ['realizedPNL', 'unrealizedPNL', 'iv', 'delta', 'theta', 'vega', 'gamma', 'riskActions'],
    rms: ['realizedPNL', 'unrealizedPNL', 'stopLoss', 'target', 'forEvery', 'profitLocking', 'profitTrailing', 'riskActions']
};

const BASE_KEYS = ['displayName', 'product', 'netQuantity', 'averagePrice', 'ltp', 'totalPNL'];

const FilterModal = ({ columns, setColumns, show, setShow, info }) => {
    const { config: { isDark } } = useContext(AppContext);

    const getActiveViews = () => {
        return Object.keys(VIEW_KEYS).filter(viewName =>
            VIEW_KEYS[viewName].every(key =>
                columns[0].positionHeader.find(item => item.accessorKey === key)?.enableColumn
            )
        );
    };

    const activeViews = getActiveViews();

    const handleChange = (e) => {
        const { name, checked, value, type } = e.target;

        const targetName = name;
        const targetValue = type === 'checkbox' ? checked : value;

        setColumns(columns.map(itemGroup => {
            const newGroup = { ...itemGroup };

            if (targetName === "tradingSymbdisp") {
                newGroup.positionHeader = newGroup.positionHeader.map(item => {
                    if (item.accessorKey === "tradingSymbol") {
                        return { ...item, enableColumn: targetValue === "tradingSymbol" };
                    }
                    if (item.accessorKey === "displayName") {
                        return { ...item, enableColumn: targetValue === "displayName" };
                    }
                    return item;
                });
            } else {
                newGroup.strategyHeader = newGroup.strategyHeader.map(item =>
                    item.accessorKey === targetName ? { ...item, enableColumn: targetValue } : item
                );
                newGroup.positionHeader = newGroup.positionHeader.map(item =>
                    item.accessorKey === targetName ? { ...item, enableColumn: targetValue } : item
                );

                const greeksKeys = ["iv", "delta", "theta", "vega", "gamma"];
                if (greeksKeys.includes(targetName) && !targetValue) {
                    const anyGreeksLeft = newGroup.positionHeader.some(item =>
                        greeksKeys.includes(item.accessorKey) && item.enableColumn
                    );
                    if (!anyGreeksLeft) {
                        newGroup.strategyHeader = newGroup.strategyHeader.map(item =>
                            item.accessorKey === "showTotalGreeks" ? { ...item, enableColumn: false } : item
                        );
                    }
                }
            }
            return newGroup;
        }));
    };

    const GroupBox = ({ title, children, flexClass = "filter-modal-flex-1" }) => (
        <div className={`filter-modal-group-box ${flexClass}`}>
            <span className="filter-modal-group-title">
                {title}
            </span>
            {children}
        </div>
    );

    const renderCheckboxes = (headerArray, keys) => {
        return headerArray
            .filter(item => keys.includes(item.accessorKey))
            .map(item => (
                <div key={item.accessorKey} className="filter-modal-checkbox-item">
                    <Checkbox
                        checked={item.enableColumn}
                        onChange={handleChange}
                        name={item.accessorKey}
                        disabled={item.accessorKey === "basketName" || item.accessorKey === "pnl"}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {item.accessorKey === "upnl" ? "Unrealized" :
                            item.accessorKey === "rpnl" ? "Realized" :
                            item.accessorKey === "roi" ? "ROI%" :
                                item.header}
                    </Checkbox>
                </div>
            ));
    };

    const applyView = (viewName) => {
        const isCurrentlyActive = activeViews.includes(viewName);
        const keys = VIEW_KEYS[viewName];

        setColumns(columns.map(itemGroup => {
            const updatedGroup = {
                ...itemGroup,
                positionHeader: itemGroup.positionHeader.map(item => {
                    if (keys.includes(item.accessorKey)) {
                        if (isCurrentlyActive) {
                            // Turning OFF: only disable if not needed by other active views and not a base key
                            const otherActiveViews = activeViews.filter(v => v !== viewName);
                            const isNeededByOthers = otherActiveViews.some(v => VIEW_KEYS[v].includes(item.accessorKey));
                            const isBaseKey = BASE_KEYS.includes(item.accessorKey);

                            if (!isNeededByOthers && !isBaseKey) {
                                return { ...item, enableColumn: false };
                            }
                        } else {
                            // Turning ON
                            return { ...item, enableColumn: true };
                        }
                    }
                    return item;
                })
            };

            const greeksKeys = ["iv", "delta", "theta", "vega", "gamma"];
            const anyGreeksLeft = updatedGroup.positionHeader.some(item =>
                greeksKeys.includes(item.accessorKey) && item.enableColumn
            );
            if (!anyGreeksLeft) {
                updatedGroup.strategyHeader = updatedGroup.strategyHeader.map(item =>
                    item.accessorKey === "showTotalGreeks" ? { ...item, enableColumn: false } : item
                );
            }

            return updatedGroup;
        }));
    };

    const activeInstrumentKey = columns[0].positionHeader?.find(
        item => (item.accessorKey === "tradingSymbol" || item.accessorKey === "displayName") && item.enableColumn
    )?.accessorKey || "displayName";

    return (
        <Modal
            title="Strategy and position columns configuration"
            open={show}
            onCancel={() => setShow(false)}
            onOk={() => setShow(false)}
            width={1150}
            footer={[
                <Button key="close" onClick={() => setShow(false)}>Close</Button>,
                <Button key="save" type="primary" onClick={() => setShow(false)} className="filter-modal-min-width-100">Save Changes</Button>
            ]}
            centered
        >
            <div className="filter-modal-section-divider">
                <span className="filter-modal-section-title">
                    View Presets
                </span>
            </div>

            <div className="filter-modal-row filter-modal-mb-20" style={{ gap: '12px', display: 'flex' }}>
                <Button
                    type={activeViews.includes('position') ? 'primary' : 'default'}
                    onClick={() => applyView('position')}
                    className="filter-modal-preset-btn"
                >
                    Position View
                </Button>
                <Button
                    type={activeViews.includes('rms') ? 'primary' : 'default'}
                    onClick={() => applyView('rms')}
                    className="filter-modal-preset-btn"
                >
                    RMS View
                </Button>
                <Button
                    type={activeViews.includes('adjustments') ? 'primary' : 'default'}
                    onClick={() => applyView('adjustments')}
                    className="filter-modal-preset-btn"
                >
                    Adjustments View
                </Button>
                <Button
                    type={activeViews.includes('greeks') ? 'primary' : 'default'}
                    onClick={() => applyView('greeks')}
                    className="filter-modal-preset-btn"
                >
                    Greeks View
                </Button>
            </div>

            <div className="filter-modal-section-divider">
                <span className="filter-modal-section-title">
                    Strategy Columns
                </span>
            </div>

            <div className="filter-modal-row filter-modal-mb-20">
                <GroupBox title="Identification" flexClass="filter-modal-flex-8">
                    <div className="filter-modal-flex-row">
                        {renderCheckboxes(columns[0].strategyHeader, ["basketName"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["combinedPremium"])}
                    </div>
                </GroupBox>
                <GroupBox title="Performance" flexClass="filter-modal-flex-9">
                    <div className="filter-modal-flex-row">
                        {renderCheckboxes(columns[0].strategyHeader, ["pnl"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["upnl"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["rpnl"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["margin"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["roi"])}
                    </div>
                </GroupBox>
                <GroupBox title="Risk Management" flexClass="filter-modal-flex-8">
                    <div className="filter-modal-flex-row">
                        {renderCheckboxes(columns[0].strategyHeader, ["sl"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["ta"])}
                        {renderCheckboxes(columns[0].strategyHeader, ["trailingSL"])}
                    </div>
                </GroupBox>
            </div>

            <div className="filter-modal-section-divider filter-modal-mt-8">
                <span className="filter-modal-section-title">
                    Position Columns
                </span>
            </div>

            <div className="filter-modal-row">
                <GroupBox title="Instrument Info" flexClass="filter-modal-flex-6">
                    <Radio.Group
                        onChange={handleChange}
                        value={activeInstrumentKey}
                        name="tradingSymbdisp"
                        className="filter-modal-display-block filter-modal-mb-8"
                    >
                        <div className="filter-modal-radio-group-vertical">
                            <Radio style={{ fontWeight: 500, fontSize: '12px' }} value="displayName">Display Name</Radio>
                            <Radio style={{ fontWeight: 500, fontSize: '12px' }} value="tradingSymbol">Trading Symbol</Radio>
                        </div>
                    </Radio.Group>
                    <div className="filter-modal-sub-divider"></div>
                    {renderCheckboxes(columns[0].positionHeader, ["name", "expiry", "strikePrice", "instrumentType"])}
                </GroupBox>

                <GroupBox title="PNL / RMS" flexClass="filter-modal-flex-4">
                    {renderCheckboxes(columns[0].positionHeader, ["realizedPNL", "unrealizedPNL", "stopLoss", "target", "forEvery", "profitLocking", "profitTrailing"])}
                </GroupBox>

                <GroupBox title="Trade Actions" flexClass="filter-modal-flex-4">
                    {renderCheckboxes(columns[0].positionHeader, ["add", "exit", "reverse", "riskONriskOff", "riskActions"])}
                </GroupBox>

                <GroupBox title="Greeks" flexClass="filter-modal-flex-4">
                    {renderCheckboxes(columns[0].positionHeader, ["iv", "delta", "theta", "vega", "gamma"])}
                    <div className="filter-modal-sub-divider" style={{ margin: '8px 0' }}></div>
                    {(() => {
                        const greeksEnabled = ["iv", "delta", "theta", "vega", "gamma"].some(key =>
                            columns[0].positionHeader.find(item => item.accessorKey === key)?.enableColumn
                        );
                        const totalGreeksItem = columns[0].strategyHeader.find(item => item.accessorKey === "showTotalGreeks");

                        return (
                            <div className="filter-modal-checkbox-item">
                                <Checkbox
                                    checked={greeksEnabled && totalGreeksItem?.enableColumn}
                                    onChange={(e) => {
                                        if (!greeksEnabled) return;
                                        handleChange(e);
                                    }}
                                    name="showTotalGreeks"
                                    disabled={!greeksEnabled}
                                >
                                    Total Greeks
                                </Checkbox>
                            </div>
                        );
                    })()}
                </GroupBox>

                <GroupBox title="Trade Details" flexClass="filter-modal-flex-6">
                    {renderCheckboxes(columns[0].positionHeader, ["totalBuyQuantity"])}
                    {renderCheckboxes(columns[0].positionHeader, ["buyAveragePrice"])}
                    {renderCheckboxes(columns[0].positionHeader, ["totalSellQuantity"])}
                    {renderCheckboxes(columns[0].positionHeader, ["sellAveragePrice"])}
                </GroupBox>
            </div>
        </Modal>
    );
};

export default FilterModal;
